import { convertUTCToLocalTime, convertLocalTimeToUTC } from './helpers';

// Use environment variables to set the API base URL
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_PRODUCTION_API_BASE_URL
  : process.env.REACT_APP_API_BASE_URL;

// Add a check for the case where the production URL might not be set
if (process.env.NODE_ENV === 'production' && !API_BASE_URL) {
  console.error('CRITICAL: REACT_APP_PRODUCTION_API_BASE_URL is not defined in the environment variables for production build.');
  // Optionally throw an error or set a default that will clearly fail
}

const fetchWithAuth = async (url, options = {}) => {
    // Get the current accessToken from localStorage
    const currentAccessToken = localStorage.getItem('accessToken');

    if (!currentAccessToken) {
        return Promise.reject(new Error('Your session has expired. You will be redirected to the login page.'));
    }

    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${currentAccessToken}`,
            },
        });

        if (response.status === 401) {
            // Attempt to refresh the token
            const newToken = await refreshAccessToken();
            if (newToken) {
                // Retry the original request with the new token
                return fetch(url, {
                    ...options,
                    headers: {
                        ...options.headers,
                        'Authorization': `Bearer ${newToken}`,
                    },
                });
            } else {
                // If refresh failed, return a rejected promise instead of throwing
                return Promise.reject(new Error('Your session has expired. You will be redirected to the login page.'));
            }
        }

        return response;
    } catch (error) {
        // If it's already our custom error, pass it through
        if (error.message === 'Your session has expired. You will be redirected to the login page.') {
            return Promise.reject(error);
        }
        // For other errors, wrap them in a rejected promise
        return Promise.reject(error);
    }
};

const refreshAccessToken = async () => {
    // Get the current refreshToken from localStorage
    const currentRefreshToken = localStorage.getItem('refreshToken');
    
    if (!currentRefreshToken) return null;

    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            // Use the currently retrieved refresh token
            body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        if (response.ok) {
            const data = await response.json();
            // Store the new accessToken
            localStorage.setItem('accessToken', data.token);
            // Return the new token for the retry logic
            return data.token;
        } else {
            // Handle token refresh failure
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('username'); // Ensure full logout
            return null;
        }
    } catch (error) {
        console.error('Error refreshing token:', error);
        return null;
    }
};

export const fetchAllContests = async () => {
    try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/contests/`);
        if (!response.ok) {
            throw new Error('Network response was not ok.');
        }
        const contests = await response.json();
        
        // Assuming contests have a 'deadline' property that needs conversion
        return contests.map(contest => ({
            ...contest,
            deadline: convertUTCToLocalTime(contest.deadline),
        }));
    } catch (error) {
        console.error('Error fetching contests:', error);
        throw error;
    }
};

export const fetchSelectedContestDetails = async (contestId) => {
    const requestUrl = `${API_BASE_URL}/api/contests/${contestId}`;
    // console.log(`API: Making actual fetch request to: ${requestUrl}`);
    try {
        const response = await fetch(requestUrl);
        if (!response.ok) {
            throw new Error('Network response was not ok.');
        }
        const contestDetails = await response.json();
        // Convert deadline from UTC to local time
        if (contestDetails.deadline) {
            contestDetails.deadline = convertUTCToLocalTime(contestDetails.deadline);
        }
        return contestDetails;
    } catch (error) {
        console.error(`Error fetching contest details for ID ${contestId}:`, error);
        throw error;
    }
};

export const addNewContest = async (formData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contests/add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });
        if (!response.ok) {
            throw new Error('Network response was not ok when adding a new contest.');
        }
        return await response.json();
    } catch (error) {
        console.error('Error adding a new contest:', error);
        throw error;
    }
};

export const saveChanges = async (contestId, updatePayload) => {
    try {
        // Convert deadline to UTC before sending
        if (updatePayload.deadline) {
            updatePayload.deadline = convertLocalTimeToUTC(updatePayload.deadline);
        }
        const response = await fetch(`${API_BASE_URL}/api/contests/update/${contestId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload),
        });
        if (!response.ok) {
            throw new Error('Network response was not ok.');
        }
        return await response.json();
    } catch (error) {
        console.error(`Error saving changes for contest ID ${contestId}:`, error);
        throw error;
    }
};

export const fetchUserData = async () => {
    try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/users/user`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            let errorText = 'Network response was not ok.';
            try {
                const errorData = await response.json();
                errorText = errorData.message || errorText;
            } catch (e) {
                // Ignore if response is not JSON
            }
            throw new Error(errorText);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching user data:', error);
        throw error;
    }
};

// Add new function to call the secure contest entry endpoint
export const enterContest = async (contestId, payload) => {
    const url = `${API_BASE_URL}/api/contests/${contestId}/enter`;

    try {
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            // --- Throw a more informative error --- 
            let errorData = null;
            let backendMessage = `Request failed with status ${response.status}`;
            try {
                // Try to get the detailed error message from the response body
                errorData = await response.json();
                backendMessage = errorData.message || errorData.error || backendMessage;
            } catch (e) {
                // If response is not JSON or empty, use the status text
                backendMessage = response.statusText || backendMessage;
                console.warn("Could not parse error response body as JSON.");
            }

            // Create a custom error object containing response details
            const error = new Error(backendMessage); 
            error.response = { // Mimic structure expected by draftUtils
                status: response.status,
                data: errorData || { message: backendMessage } // Attach parsed data or fallback
            };
            throw error; // Throw the enhanced error object
            // -----------------------------------------
        }

        // If response is OK, return the parsed JSON body
        return await response.json(); 

    } catch (error) {
        // Log the error caught here (could be fetch error or the one we threw)
        console.error('Error in enterContest API call:', error);
        // Re-throw the error to be caught by the caller (handleSubmit)
        throw error; 
    }
};

/**
 * Sends a "fire-and-forget" request to update the user's last activity timestamp.
 * Doesn't throw errors, but will log them to the console.
 */
export const updateUserActivity = async () => {
    // No need to proceed if there's no token.
    if (!localStorage.getItem('accessToken')) {
        return;
    }

    try {
        // Use fetchWithAuth to handle authentication and token refresh automatically.
        const response = await fetchWithAuth(`${API_BASE_URL}/api/users/activity`, {
            method: 'POST',
        });

        // We don't need to do anything with the response unless it's an error,
        // but fetchWithAuth will throw on network errors or failed refresh,
        // which will be caught below.
        if (!response.ok) {
           // We can log this, but don't want to disrupt the user experience.
           console.error(`Activity update failed with status: ${response.status}`);
        }

    } catch (error) {
        // Log the error but don't re-throw it, as this is a background task.
        console.error('Failed to update user activity:', error.message);
    }
};

// Modify the withdrawContestEntry function to provide better error messages
export const withdrawContestEntry = async (contestId, entryName) => {
    const url = `${API_BASE_URL}/api/contests/${contestId}/withdraw`;
    const payload = { entryName };

    try {
        const response = await fetchWithAuth(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            let errorText = 'Failed to withdraw entry';
            try {
                const errorData = await response.json();
                errorText = errorData.message || errorText;
                console.log('API Error Response:', errorData); // Debug log
            } catch (e) { 
                console.log('Failed to parse error response:', e); // Debug log
            }
            
            // Add more context to the error message
            if (response.status === 401) {
                errorText = 'Your session has expired. You will be redirected to the login page.';
                console.log('Setting 401 error message:', errorText); // Debug log
            } else if (response.status === 403) {
                errorText = 'You are not authorized to perform this action.';
            } else if (response.status === 404) {
                errorText = 'Entry not found. It may have already been withdrawn.';
            }
            
            const error = new Error(errorText);
            console.log('Throwing error with message:', error.message); // Debug log
            throw error;
        }

        return await response.json();
    } catch (error) {
        console.error('Error in withdrawContestEntry API call:', error);
        // Ensure we're throwing the error with the correct message
        if (error.message === 'Failed to withdraw entry' && error.response?.status === 401) {
            error.message = 'Your session has expired. You will be redirected to the login page.';
        }
        throw error;
    }
};

const apiService = {
    fetchAllContests,
    fetchSelectedContestDetails,
    addNewContest,
    saveChanges,
    fetchUserData,
    enterContest,
    // Add the new function to the service object
    withdrawContestEntry, 
    updateUserActivity,
};

export default apiService;